import { RootState } from "typedefs";
import { useEffect } from "react";
import { useActions, useSelector } from "app/utils/hooks";
import { fetchEvents } from "../../actions/events";
import { fetchTimeZones } from "app/actions/scheduling/timeZone";
import { DateTime } from "luxon";

export interface EventsFilter {
  // CN-1323 (P7): scope a head's bookings list to one dependent (external id)
  // or to the whole family. Omitted = the caller's own bookings.
  participantAthleteId?: string | null;
  includeFamily?: boolean;
}

export const useEventsModel = (
  startDate: DateTime,
  endDate: DateTime,
  filter: EventsFilter = {},
) => {
  const eventActions = useActions({
    fetchEvents,
  });

  const start: string = startDate.toISODate();
  const end: string = endDate.toISODate();
  const participantAthleteId = filter.participantAthleteId ?? null;
  const includeFamily = filter.includeFamily ?? false;

  useEffect(() => {
    const params: { [key: string]: string | boolean } = { start, end };
    if (participantAthleteId) {
      params.participant_athlete_id = participantAthleteId;
    } else if (includeFamily) {
      params.include_family = true;
    }
    eventActions.fetchEvents(params);
  }, [start, end, participantAthleteId, includeFamily]);

  const events = useSelector((state: RootState) => state.events);

  return { events };
};

export const useTimeZonesModel = () => {
  const actions = useActions({
    fetchTimeZones,
  });

  const timeZones = useSelector(
    (state: RootState) => state.schedulingTimeZones,
  );

  /**
   * CFTB-3439 follow-up: fetch once, not on every mount.
   *
   * The list is a fixed reference table held in a single slice, and every mount used
   * to refetch it — which flips the slice's shared `pending` to true. A screen that
   * gates its render on `pending` while rendering another component that calls this
   * hook therefore unmounts that component the moment it mounts, refetches, renders
   * it again, and loops: the public coach page span 800+ requests behind a permanent
   * spinner that way.
   *
   * Skipped when the data is already loaded, so mounting a second consumer is free.
   */
  const loaded = timeZones.data.length > 0;

  useEffect(() => {
    if (loaded) {
      return;
    }
    actions.fetchTimeZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  return timeZones;
};
